import { StateCreator } from "zustand";
import { produce } from "immer";
import { STEPS_ENUM } from "@widgets/StepsManager/store/types";
import { stepsStore } from "@widgets/StepsManager/store/stepsStore";

import * as Sentry from "@sentry/browser";
import { ExternalLoginState, ExternalLoginStateEnum } from "@entities/LoginComponent/LoginComponent";
import {
	FxIdApplicationHandlersAuthAuthenticateWithExternalIdState,
	FxIdWebFeaturesAuthAuthenticateWithExternalIdResponse
} from "@/api/gen";
import { appStore } from "@/app/store/appStore";
import { AuthenticateUser } from "@/api/OpenApiClient";
import { setAuthToken } from "@/api/Auth";
import { codeStepStore } from "@/entities/StepCode/store/codeStepStore";
import { folderStepStore } from "@/entities/StepFolder/store/folderStepStore";
import { wrapStore } from "@/app/store/StoreWrapper";
import { IOtherLogin, persistStore } from "@entities/StepFolder/store/persistStore";

export interface IEmailStepStore {
	responseData: FxIdWebFeaturesAuthAuthenticateWithExternalIdResponse | null;

	email: string | null;
	login: string | null;
	setLogin: (login: string | null) => void;
	setEmail: (email: string) => void;

	isLoading: boolean;

	startRequest: (refreshToken?:string,connectEmail?:boolean) => void;
	// fetchData: () => Promise<void>;

	error: string | null;
	hint: string;
	setHint: (hint: string) => void;
	clearError: () => void;
	checkResponse: (res: FxIdWebFeaturesAuthAuthenticateWithExternalIdResponse) => void;

	valueError: string | null;
	logout: () => void;
	setIsLoading: (isLoading: boolean) => void;
	externalLoginState: ExternalLoginState;
	isExternalLogin: boolean;
	setIsExternalLogin: (value: boolean) => void;
	setExternalLoginState: (externalLoginState: ExternalLoginState) => void;
	preinstallPath: string | null;
	setPreinstallPath: (path: string) => void;
	saveCurrentLogin: (removeLogin?: IOtherLogin) => void;
}

export const validateEmail = (email: string) => {
	return email.match(
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
	);
};

function emailErrors(email: string) {
	return email.length>0 ? (validateEmail(email) ? null : "Invalid email"):null;
}

export const createEmailStepStore: StateCreator<IEmailStepStore> = (set) => ({
	externalLoginState: { state: ExternalLoginStateEnum.notReady },
	login: null,
	setLogin: (login: string | null) => {
		set(
			produce((store: IEmailStepStore) => {
				store.login = login;
			})
		);
	},
	preinstallPath: null,
	isExternalLogin: false,
	setIsExternalLogin: (value: boolean) => {
		set(
			produce((store: IEmailStepStore) => {
				store.isExternalLogin = value;
			})
		);
	},
	setPreinstallPath: (path: string) => {
		set(
			produce((store: IEmailStepStore) => {
				store.preinstallPath = path;
			})
		);
	},
	setExternalLoginState: (externalLoginState: ExternalLoginState) => {
		set(
			produce((store: IEmailStepStore) => {
				store.externalLoginState = externalLoginState;
			})
		);
	},
	logout: async () => {
		const { setActiveStep } = stepsStore.getState();
		const { setHint, setLogin } = emailStepStore.getState();
		persistStore.getState().setRefreshToken(null);
		setAuthToken(null);
		folderStepStore.getState().setBranches([]);
		setHint("");
		setLogin(null);
		setActiveStep(STEPS_ENUM.EmailStep);

		set(
			produce((store: IEmailStepStore) => {
				store.responseData = null;
			})
		);
		//appStore.getState().setLoginFlow(true);
	},
	saveCurrentLogin: async (removeLogin?: IOtherLogin) => {
		const { setOtherLogins, otherLogins, refreshToken } = persistStore.getState();
		const { appCode } = appStore.getState();
		const { login } = emailStepStore.getState();
		let logins = otherLogins;
		//console.log(removeLogin);

		if (removeLogin) {
			logins = otherLogins.filter(
				(item) => !(item.username === removeLogin.username && item.appCode === appCode)
			);
		}
		if (refreshToken[appCode] && login)
			logins = logins.concat([
				{
					appCode: appCode,
					username: login,
					lastActive: new Date(),
					refreshToken: refreshToken[appCode]
				}
			]);
		setOtherLogins(logins);
	},
	valueError: null,
	setIsLoading: (isLoading: boolean) => {
		set(
			produce((store: IEmailStepStore) => {
				store.isLoading = isLoading;
			})
		);
	},
	hint: "",
	setHint: (hint: string) => {
		set(
			produce((store: IEmailStepStore) => {
				store.hint = hint;
			})
		);
	},
	email: null,
	setEmail: (email: string) => {
		set(
			produce((store: IEmailStepStore) => {
				store.valueError = emailErrors(email);
				store.email = email;
			})
		);
		try {
			Sentry.setUser({ email });
		} catch (error) {
			console.error("Error setting user:", error);
		}
	},

	responseData: null,
	isLoading: false,
	error: null,
	clearError: () => {
		set(
			produce((store: IEmailStepStore) => {
				store.error = null;
			})
		);
	},
	startRequest: async (refreshToken?:string,connectEmail?:boolean): Promise<void> => {
		const { appCode, loaderName } = appStore.getState();

		const { setHint, checkResponse, email, valueError } = emailStepStore.getState();
		const { isPrivacy } = persistStore.getState();
		const { code, setCode } = codeStepStore.getState();



		const token =  refreshToken || persistStore.getState().refreshToken[appCode];
		console.log("!!!! startRequest ",appCode,token);
		const currentLang = persistStore.getState().language; // localStorage.getItem("i18nextLng");

		if (valueError)
		{
			console.log(`startRequest valueError ${valueError}`);
			return;
		}
		if (!email && !token)
		{
			console.log(`startRequest no token ${token} or email ${email}`);
            return;
		}
		if (!token && !isPrivacy)
		{
			console.log(`startRequest No privacy ${isPrivacy}`);
			return;
		}
		const request = {
			GameSystemName: appCode,
			Locale: currentLang,
			SessionId: loaderName,
			ExternalSocialIdentifier: undefined,
			RefreshToken: token,
			AdditionalOptions: { AllowOnlyNewEmail: connectEmail},
			EmailCredentials:

				email && !emailErrors(email)
					? {
							Email: email,
							EmailCode: code
						}
					: undefined
		};
		setCode(null);
		set(
			produce((store: IEmailStepStore) => {
				store.isLoading = true;
				store.error = null;
			})
		);

		try {
			const newResponse = await AuthenticateUser(request, appCode);

			setHint(newResponse.Hint);
			checkResponse(newResponse);
		} catch (err) {
			console.log(`⚡ произошла ошибка, что-то пошло не так...${err}`);

			set(
				produce((store: IEmailStepStore) => {
					store.error = "что-то пошло не так...";
				})
			);
			throw new Error("что-то пошло не так...");
		} finally {
			set(
				produce((store: IEmailStepStore) => {
					store.isLoading = false;
				})
			);
		}
	},
	checkResponse: async (res: FxIdWebFeaturesAuthAuthenticateWithExternalIdResponse) => {
		set(
			produce((store: IEmailStepStore) => {
				store.responseData = res;
			})
		);
		const { setActiveStep } = stepsStore.getState();
		const { setEmail } = emailStepStore.getState();
		const { setIsPrivacy } = persistStore.getState();
		if (res.Email) {
			setEmail(res.Email);
		}

		if (res.Tokens?.RefreshToken) {
			persistStore.getState().setRefreshToken(res.Tokens.RefreshToken);
		}
		switch (res?.State) {
			//ToEmailStep
			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedAccountWithoutEmail:
				setActiveStep(STEPS_ENUM.EmailStepConnect);
				// Временный костыль, убрать когда бекенд поправит сообщение
				emailStepStore.getState().setHint("");
				appStore.getState().setLoginFlow(true);
				break;

			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedInviteError:
				setActiveStep(STEPS_ENUM.EmailStep);
				appStore.getState().setLoginFlow(true);
				break;

			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedExternalIdWithoutEmail:
				setActiveStep(STEPS_ENUM.EmailStep);
				appStore.getState().setLoginFlow(true);
				break;

			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedNoCredentials:
				setActiveStep(STEPS_ENUM.EmailStep);
				appStore.getState().setLoginFlow(true);
				break;

			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedTooMuchRequestsForInvite:
				setActiveStep(STEPS_ENUM.EmailStep);
				appStore.getState().setLoginFlow(true);
				break;

			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedUnknownError:
				setActiveStep(STEPS_ENUM.EmailStep);
				appStore.getState().setLoginFlow(true);
				break;

			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedValidationError:
				setActiveStep(STEPS_ENUM.EmailStep);
				appStore.getState().setLoginFlow(true);
				break;

			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedWrongEmailForExternalId:
				setActiveStep(STEPS_ENUM.EmailStep);
				appStore.getState().setLoginFlow(true);
				break;

			//ToCodeStep
			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedNeedEmailCode:
				setActiveStep(STEPS_ENUM.CodeStep);
				appStore.getState().setLoginFlow(true);
				setIsPrivacy(true);
				break;

			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.FailedWrongCode:
				setActiveStep(STEPS_ENUM.CodeStep);
				appStore.getState().setLoginFlow(true);
				setIsPrivacy(true);
				break;

			//ToFolderStep
			case FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.Success:
				if (res.Tokens?.AccessToken) {
					console.log(`⚡ записываю AccessToken для ${res.Email}`);
					console.log(res.Tokens.AccessToken);
					setAuthToken(res.Tokens.AccessToken);


					if (res.Email) {
						emailStepStore.getState().setLogin(res.Email);
					}

				} else {

					console.error("no access token");
					return;
				}

				//setActiveStep(STEPS_ENUM.LoginSelectStep);

				if (res.TransferOptions?.AllowedTransferFromMyGames) {
					setActiveStep(STEPS_ENUM.ConnectStep);
					appStore.getState().setLoginFlow(true);
					break;
				}
				console.log(`⚡ appStore.getState().setLoginFlow(false)gp`);
				appStore.getState().setLoginFlow(false);


				break;

			default:
				break;
		}
	}
});

export const emailStepStoreWrapper = wrapStore<IEmailStepStore>();
export const emailStepStore = emailStepStoreWrapper.withStore(createEmailStepStore, "email-step-store");
